import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './collection-modal.html';
import type { Collection, MediaItem, MediaVisibility } from '../services/api.types';
import { MediaType, MediaTypeFilter } from '../services/api.types';
import { getDefaultVisibility } from '../services/default-visibility.service';
import {
  createCollection,
  deleteCollectionLocationMap,
  generateCollectionLocationMap,
  getCollectionLocationMapUrl,
  getCollection,
  getMediaItem,
  getMediaPreviewUrl,
  listMediaItems,
  updateCollection,
} from '../services/api.service';
import { ConfirmDialog } from './confirm-dialog';

@customElement({ name: 'collection-modal', template })
export class CollectionModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: (collection: Collection) => void;
  @bindable public closed!: () => void;
  @bindable public collectionToEdit: Collection | null = null;
  @bindable public parentId: string | null = null;
  @bindable public selectedMediaItems: MediaItem[] = [];

  public title = '';
  public subtitle = '';
  public collectionDateFrom = '';
  public collectionDateTo = '';
  public dateRangeEnabled = false;
  public visibility: MediaVisibility = 'private';
  public coverMediaItemId: string | null = null;
  public coverMediaItem: MediaItem | null = null;
  public hideCoverInGallery = false;
  public saving = false;
  public fillingDates = false;
  public error = '';

  public showCoverPicker = false;
  public coverSearchText = '';
  public coverResults: MediaItem[] = [];
  public coverLoading = false;
  private _coverSearchDebounce: ReturnType<typeof setTimeout> | null = null;
  private _coverCollectionId: string | null = null;
  private _coverCollectionItems: MediaItem[] | null = null;
  private _coverCollectionLoad: { id: string; promise: Promise<MediaItem[]> } | null = null;
  private _coverSearchVersion = 0;

  public hasLocationMap = false;
  public locationMapGeneratedAt: string | null = null;
  public locationMapGenerating = false;
  public locationMapError = '';

  private _keyHandler!: (e: KeyboardEvent) => void;

  public get isEditMode(): boolean { return this.collectionToEdit !== null; }

  public getMediaPreviewUrl = getMediaPreviewUrl;

  public get canHideCoverInGallery(): boolean {
    return this.coverMediaItem?.mediaType === MediaType.Image;
  }

  public get locationMapImageUrl(): string {
    return getCollectionLocationMapUrl(this.collectionToEdit!.id, this.locationMapGeneratedAt ?? undefined);
  }

  public collectionToEditChanged(collection: Collection | null): void {
    this._initializeForm(collection);
  }

  public attached(): void {
    this._initializeForm(this.collectionToEdit);
    this._keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') this.close(); };
    document.addEventListener('keydown', this._keyHandler);
  }

  private _initializeForm(collection: Collection | null): void {
    this.title = collection?.title ?? '';
    this.subtitle = collection?.subtitle ?? '';
    this.collectionDateFrom = collection?.collectionDate
      ? collection.collectionDate.substring(0, 10)
      : '';
    this.collectionDateTo = collection?.collectionDateTo
      ? collection.collectionDateTo.substring(0, 10)
      : '';
    this.dateRangeEnabled = !!this.collectionDateFrom && !!this.collectionDateTo;
    this.coverMediaItemId = collection?.coverMediaItemId ?? null;
    this.coverMediaItem = null;
    this.hideCoverInGallery = collection?.hideCoverInGallery ?? false;
    this.visibility = collection
      ? (collection.visibility === 'family' ? 'family' : 'private')
      : getDefaultVisibility();
    this.hasLocationMap = collection?.hasLocationMap ?? false;
    this.locationMapGeneratedAt = collection?.locationMapGeneratedAt ?? null;
    this.error = '';
    this.fillingDates = false;
    this.locationMapError = '';
    this.showCoverPicker = false;
    this.coverSearchText = '';
    this.coverResults = [];
    this.coverLoading = false;
    this._coverSearchVersion++;

    // Pre-fetch cover preview reference so the thumbnail can render immediately.
    if (this.coverMediaItemId) void this._loadCoverPreviewInfo();
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
    if (this._coverSearchDebounce) clearTimeout(this._coverSearchDebounce);
  }

  public close(): void { this.closed(); }

  public openCoverPicker(): void {
    this.showCoverPicker = true;
    this.coverSearchText = '';
    void this._searchCoverImages();
  }

  public closeCoverPicker(): void {
    this.showCoverPicker = false;
  }

  public onVisibilitySelectChanged(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.visibility = value === 'family' ? 'family' : 'private';
  }

  public onDateRangeChanged(event: Event): void {
    this.dateRangeEnabled = (event.target as HTMLInputElement).checked;
    if (!this.dateRangeEnabled)
      this.collectionDateTo = '';
  }

  public clearDates(): void {
    this.collectionDateFrom = '';
    this.collectionDateTo = '';
    this.dateRangeEnabled = false;
  }

  public async fillDatesFromMedia(): Promise<void> {
    if (this.fillingDates || this.saving) return;

    this.error = '';
    this.fillingDates = true;
    try {
      const items = this.isEditMode && this.collectionToEdit
        ? await this._loadCurrentCollectionMediaItems(this.collectionToEdit.id)
        : this.selectedMediaItems;
      const dates = items
        .map(item => this._capturedDatePart(item.capturedAt))
        .filter((date): date is string => date !== null)
        .sort();

      if (dates.length === 0) {
        this.error = this.i18n.tr('collections.modal.dateFillNoDates');
        return;
      }

      this.collectionDateFrom = dates[0];
      const latestDate = dates[dates.length - 1];
      if (latestDate !== this.collectionDateFrom) {
        this.collectionDateTo = latestDate;
        this.dateRangeEnabled = true;
      } else {
        this.collectionDateTo = '';
        this.dateRangeEnabled = false;
      }
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.fillingDates = false;
    }
  }

  public coverSearchTextChanged(): void {
    if (this._coverSearchDebounce) clearTimeout(this._coverSearchDebounce);
    this._coverSearchDebounce = setTimeout(() => { void this._searchCoverImages(); }, 300);
  }

  public pickCover(item: MediaItem): void {
    this.coverMediaItemId = item.id;
    this.coverMediaItem = item;
    if (item.mediaType !== MediaType.Image)
      this.hideCoverInGallery = false;
    this.showCoverPicker = false;
  }

  public isVideo(item: MediaItem): boolean {
    return item.mediaType === MediaType.Video;
  }

  public coverItemLabel(item: MediaItem): string {
    const label = item.title || item.fileName;
    return this.isVideo(item)
      ? `${label} (${this.i18n.tr('collections.modal.coverVideo')})`
      : label;
  }

  public clearCover(): void {
    this.coverMediaItemId = null;
    this.coverMediaItem = null;
    this.hideCoverInGallery = false;
  }

  private async _searchCoverImages(): Promise<void> {
    const searchVersion = ++this._coverSearchVersion;
    this.coverLoading = true;
    try {
      let items: MediaItem[];
      const coverCollectionId = this.collectionToEdit?.id ?? this.parentId;
      if (!this.isEditMode && this.selectedMediaItems.length > 0) {
        items = this.selectedMediaItems;
      } else if (coverCollectionId) {
        items = await this._loadCurrentCollectionMediaItems(coverCollectionId);
      } else {
        const result = await listMediaItems({
          mediaType: MediaTypeFilter.All,
          searchText: this.coverSearchText.trim() || undefined,
          page: 1,
          pageSize: 60,
          sortBy: 'capturedat',
          sortDescending: true,
        });
        items = result.mediaItems;
      }
      if (searchVersion !== this._coverSearchVersion) return;

      const query = this.coverSearchText.trim().toLocaleLowerCase();
      this.coverResults = items.filter(item =>
        this._isSelectableCover(item) &&
        (!query ||
          item.title.toLocaleLowerCase().includes(query) ||
          item.fileName.toLocaleLowerCase().includes(query)));
    } catch {
      if (searchVersion !== this._coverSearchVersion) return;
      this.coverResults = [];
    } finally {
      if (searchVersion === this._coverSearchVersion)
        this.coverLoading = false;
    }
  }

  private _isSelectableCover(item: MediaItem): boolean {
    return item.mediaType === MediaType.Image
      || (item.mediaType === MediaType.Video && item.hasPreview);
  }

  private async _loadCurrentCollectionMediaItems(collectionId: string): Promise<MediaItem[]> {
    if (this._coverCollectionId === collectionId && this._coverCollectionItems)
      return this._coverCollectionItems;
    if (this._coverCollectionLoad?.id === collectionId)
      return this._coverCollectionLoad.promise;

    const promise = (async (): Promise<MediaItem[]> => {
      const pageSize = 100;
      const items: MediaItem[] = [];
      let page = 1;
      let totalCount = 0;

      do {
        const detail = await getCollection(collectionId, {
          page,
          pageSize,
          sortBy: 'capturedat',
          sortDescending: true,
        });
        items.push(...detail.mediaItems);
        totalCount = detail.mediaItemsTotalCount;
        if (detail.mediaItems.length === 0) break;
        page++;
      } while (items.length < totalCount);

      this._coverCollectionId = collectionId;
      this._coverCollectionItems = items;
      return items;
    })();
    this._coverCollectionLoad = { id: collectionId, promise };
    try {
      return await promise;
    } finally {
      if (this._coverCollectionLoad?.promise === promise)
        this._coverCollectionLoad = null;
    }
  }

  private async _loadCoverPreviewInfo(): Promise<void> {
    const coverMediaItemId = this.coverMediaItemId;
    if (!coverMediaItemId) return;
    try {
      const mediaItem = await getMediaItem(coverMediaItemId);
      if (this.coverMediaItemId === coverMediaItemId)
        this.coverMediaItem = mediaItem;
    } catch {
      if (this.coverMediaItemId === coverMediaItemId)
        this.coverMediaItem = null;
    }
  }

  private _capturedDatePart(capturedAt: string | null): string | null {
    if (!capturedAt) return null;
    const capturedDate = new Date(capturedAt);
    if (Number.isNaN(capturedDate.getTime()))
      return null;

    const year = capturedDate.getFullYear();
    const month = String(capturedDate.getMonth() + 1).padStart(2, '0');
    const day = String(capturedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  public async generateLocationMap(): Promise<void> {
    if (!this.collectionToEdit) return;
    this.locationMapError = '';
    this.locationMapGenerating = true;
    try {
      const updated = await generateCollectionLocationMap(this.collectionToEdit.id);
      this.hasLocationMap = updated.hasLocationMap;
      this.locationMapGeneratedAt = updated.locationMapGeneratedAt;
      this.collectionToEdit = updated;
    } catch (e: unknown) {
      this.locationMapError = e instanceof Error ? e.message : String(e);
    } finally {
      this.locationMapGenerating = false;
    }
  }

  public async deleteLocationMap(): Promise<void> {
    if (!this.collectionToEdit) return;
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('collections.modal.locationMapDeleteConfirm'),
      this.i18n.tr('collections.modal.locationMapDeleteTitle'),
    );
    if (!confirmed) return;

    this.locationMapError = '';
    this.locationMapGenerating = true;
    try {
      const updated = await deleteCollectionLocationMap(this.collectionToEdit.id);
      this.hasLocationMap = updated.hasLocationMap;
      this.locationMapGeneratedAt = updated.locationMapGeneratedAt;
      this.collectionToEdit = updated;
    } catch (e: unknown) {
      this.locationMapError = e instanceof Error ? e.message : String(e);
    } finally {
      this.locationMapGenerating = false;
    }
  }

  public async save(): Promise<void> {
    this.error = '';
    if (!this.title.trim()) {
      this.error = this.i18n.tr('collections.modal.titleRequired');
      return;
    }

    this.saving = true;
    try {
      let collection: Collection;
      if (this.dateRangeEnabled && this.collectionDateTo && !this.collectionDateFrom) {
        this.error = this.i18n.tr('collections.modal.dateFromRequired');
        return;
      }
      if (this.dateRangeEnabled && this.collectionDateFrom && this.collectionDateTo
        && this.collectionDateTo < this.collectionDateFrom) {
        this.error = this.i18n.tr('collections.modal.dateRangeInvalid');
        return;
      }

      const collectionDate = this.collectionDateFrom
        ? this._dateInputToIso(this.collectionDateFrom)
        : null;
      const collectionDateTo = this.dateRangeEnabled && this.collectionDateTo
        ? this._dateInputToIso(this.collectionDateTo)
        : null;
      if (this.isEditMode && this.collectionToEdit) {
        const originalCoverId = this.collectionToEdit.coverMediaItemId;
        const clearCover = originalCoverId != null && this.coverMediaItemId == null;
        collection = await updateCollection(this.collectionToEdit.id, {
          title: this.title.trim(),
          subtitle: this.subtitle.trim(),
          collectionDate,
          collectionDateTo,
          clearCollectionDate: collectionDate === null,
          clearCollectionDateTo: collectionDateTo === null,
          coverMediaItemId: this.coverMediaItemId ?? undefined,
          hideCoverInGallery: this.hideCoverInGallery,
          clearCover,
          visibility: this.visibility,
        });
      } else {
        collection = await createCollection({
          title: this.title.trim(),
          subtitle: this.subtitle.trim(),
          collectionDate,
          collectionDateTo,
          parentId: this.parentId,
          coverMediaItemId: this.coverMediaItemId ?? undefined,
          hideCoverInGallery: this.hideCoverInGallery,
          visibility: this.visibility,
        });
      }
      this.saved(collection);
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }

  private _dateInputToIso(value: string): string {
    return new Date(`${value}T00:00:00Z`).toISOString();
  }
}
